import { useNavigation, usePreventRemove } from "@react-navigation/native";
import type { NavigationAction } from "@react-navigation/routers";
import { useFocusEffect } from "expo-router";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode
} from "react";
import {
  Animated,
  Easing,
  Platform,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
  type StyleProp,
  type ViewStyle
} from "react-native";
import {
  KeyboardAwareScrollView,
  type KeyboardAwareScrollViewRef
} from "react-native-keyboard-controller";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { HeaderContentMask } from "@/components/HeaderContentMask";
import { HeaderBackdropScrim } from "@/components/HeaderBackdropScrim";
import { PageBackdrop, usePageBackdrop } from "@/components/PageBackdrop";
import { getAppHeaderHeight } from "@/design/headerMetrics";
import { SEARCH_KEYBOARD_GAP } from "@/design/keyboardMetrics";
import {
  getWebNavigationDirection,
  getWebNavigationFadeReveal
} from "@/design/navigationMotion";
import {
  dispatchWebNavigationWithoutDirection,
  registerWebRiseLayer
} from "@/design/navigationMotionState";
import { Overline, Text } from "@/design/Text";
import { contentMaxWidth, fonts, palette, radius, space } from "@/design/tokens";

/**
 * Scrollable screen with the app canvas and a centered content column on
 * wide viewports.
 */
/**
 * Soft page transition, replayed on every focus (forward AND back
 * navigation). One gesture, both directions: the page materializes as part
 * of the glide. Both channels share an ease-out personality — opacity rises
 * from nothing fast enough to read the content almost immediately, then
 * drifts the last stretch to full while the longer transform settles.
 */
const webTransformEase = Easing.bezier(0.215, 0.61, 0.355, 1);
const webOpacityEase = Easing.bezier(0.33, 1, 0.68, 1);
const WEB_ENTER_DISTANCE = 56;
const WEB_ENTER_DIM = 0;
const WEB_OPACITY_MS = 500;
const WEB_RISE_DISTANCE = 24;
const WEB_LAYER_MS = 300;
type EnterTransition = "lateral" | "rise";
type ScreenLeave = (onComplete: () => void) => boolean;
type NativeTransitionEndNavigation = {
  addListener: (
    event: "transitionEnd",
    listener: (event: { data: { closing: boolean } }) => void
  ) => () => void;
};
// The only breathing room below the header is its own paddingBottom; no extra
// gap stacked on top of it, so the space under the controls matches the space
// above them instead of reading top-heavy.
export const APP_HEADER_CONTENT_GAP = 0;

function useEnterTransitionController(variant: EnterTransition = "lateral") {
  // Focus effects are passive — the browser may paint before they run. So
  // whatever the first paint shows must already BE the entrance state, or a
  // busy frame flashes the settled page before snapping to its offset (the
  // intermittent "jitter"). Two guarantees close that race:
  // - screens are BORN at their entrance offset (direction is recorded at
  //   dispatch, before the destination ever renders);
  // - a screen parked in the stack blurs to invisible, so revealing it later
  //   can never paint its stale resting frame before the effect replays.
  const mountDirection = Platform.OS === "web" ? getWebNavigationDirection() : 0;
  const mountFadeReveal =
    Platform.OS === "web" && mountDirection === 0 && getWebNavigationFadeReveal();
  const enter = useRef(
    new Animated.Value(
      Platform.OS === "web" && mountDirection !== 0
        ? variant === "rise"
          ? WEB_RISE_DISTANCE
          : mountDirection * WEB_ENTER_DISTANCE
        : 0
    )
  ).current;
  const webOpacity = useRef(
    new Animated.Value(mountDirection === 0 && !mountFadeReveal ? 1 : WEB_ENTER_DIM)
  ).current;
  const activeAnimation = useRef<Animated.CompositeAnimation | null>(null);
  const leaveCompletions = useRef<(() => void)[]>([]);
  const leaving = useRef(false);

  useFocusEffect(
    useCallback(() => {
      leaving.current = false;
      leaveCompletions.current = [];
      if (Platform.OS === "web") {
        const direction = getWebNavigationDirection();
        const fadeReveal = direction === 0 && getWebNavigationFadeReveal();
        let animation: Animated.CompositeAnimation | null = null;
        if (direction === 0) {
          enter.setValue(0);
          if (fadeReveal) {
            webOpacity.setValue(WEB_ENTER_DIM);
            animation = Animated.timing(webOpacity, {
              toValue: 1,
              duration: WEB_LAYER_MS,
              easing: Easing.out(Easing.cubic),
              useNativeDriver: false
            });
            activeAnimation.current = animation;
            animation.start();
          } else {
            webOpacity.setValue(1);
          }
        } else {
          enter.setValue(
            variant === "rise" ? WEB_RISE_DISTANCE : direction * WEB_ENTER_DISTANCE
          );
          webOpacity.setValue(WEB_ENTER_DIM);
          animation = Animated.parallel([
            Animated.timing(enter, {
              toValue: 0,
              duration: variant === "rise" ? WEB_OPACITY_MS : 1000,
              easing: webTransformEase,
              useNativeDriver: false
            }),
            Animated.timing(webOpacity, {
              toValue: 1,
              duration: WEB_OPACITY_MS,
              easing: webOpacityEase,
              useNativeDriver: false
            })
          ]);
          activeAnimation.current = animation;
          animation.start();
        }
        return () => {
          animation?.stop();
          activeAnimation.current?.stop();
          activeAnimation.current = null;
          leaveCompletions.current = [];
          leaving.current = false;
          webOpacity.setValue(0);
        };
      }
      // Native Stack already owns push/pop motion. A second route-level
      // fade/translate here caused double motion and header flicker,
      // especially on iOS 15's older compositor.
      enter.setValue(0);
      return undefined;
    }, [enter, variant, webOpacity])
  );

  const leave = useCallback<ScreenLeave>((onComplete) => {
    if (Platform.OS !== "web" || variant !== "rise") return false;
    leaveCompletions.current.push(onComplete);
    if (leaving.current) return true;

    leaving.current = true;
    activeAnimation.current?.stop();
    const animation = Animated.parallel([
      Animated.timing(enter, {
        toValue: WEB_RISE_DISTANCE,
        duration: WEB_LAYER_MS,
        easing: Easing.inOut(Easing.cubic),
        useNativeDriver: false
      }),
      Animated.timing(webOpacity, {
        toValue: WEB_ENTER_DIM,
        duration: WEB_LAYER_MS,
        easing: Easing.inOut(Easing.cubic),
        useNativeDriver: false
      })
    ]);
    activeAnimation.current = animation;
    animation.start(({ finished }) => {
      if (activeAnimation.current === animation) activeAnimation.current = null;
      if (finished) {
        const completions = leaveCompletions.current;
        leaveCompletions.current = [];
        completions.forEach((complete) => complete());
      } else {
        leaveCompletions.current = [];
        leaving.current = false;
      }
    });
    return true;
  }, [enter, variant, webOpacity]);

  if (Platform.OS === "web") {
    return {
      leave,
      style: {
        opacity: webOpacity,
        transform: variant === "rise" ? [{ translateY: enter }] : [{ translateX: enter }],
        willChange: "transform, opacity"
      }
    };
  }

  return { leave, style: {} };
}

/** Existing style-only hook used by custom screens such as Watch. */
export function useEnterTransition() {
  return useEnterTransitionController().style;
}

export function Screen({
  children,
  wide = false,
  padded = true,
  backdropUri = null,
  backdropFocalY = null,
  header,
  enter = "lateral",
  keyboardAware = false,
  keyboardShouldPersistTaps,
  contentBottomInset
}: PropsWithChildren<{
  wide?: boolean;
  padded?: boolean;
  backdropUri?: string | null;
  /**
   * Vertical focal anchor for this page's backdrop artwork, 0-100. Omit for the
   * centred default. Only pass it where the artwork demands it — a program
   * cover, whose wordmark sits mid-frame (COVER_ARTWORK_FOCAL_Y). Pages backed
   * by brand art or stage stills must stay centred.
   */
  backdropFocalY?: number | null;
  header?: ReactNode;
  enter?: EnterTransition;
  keyboardAware?: boolean;
  keyboardShouldPersistTaps?: "always" | "never" | "handled";
  /** Total scroll-content padding below the page, including any safe area. */
  contentBottomInset?: number;
}>) {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const scrollRef = useRef<ScrollView>(null);
  const keyboardAwareScrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const enterTransition = useEnterTransitionController(enter);
  const enterStyle = enterTransition.style;
  const leaveTransition = enterTransition.leave;
  const pendingRemovalAction = useRef<NavigationAction | null>(null);
  const dismissalDoneRef = useRef<(() => void) | null>(null);
  const [removalAllowed, setRemovalAllowed] = useState(false);
  const viewport = useWindowDimensions();
  const maxWidth = contentMaxWidth(viewport.width, wide);
  // AppHeader has fixed control and safe-area geometry. Measuring that same
  // header after mount creates a second vertical layout on native when Yoga's
  // measured value differs by even a fraction of a pixel. Keep the mask,
  // scroll inset, and backdrop scrim on the deterministic shared metric from
  // the very first frame, as Watch already does for its player stage.
  const resolvedHeaderHeight = getAppHeaderHeight(insets.top);
  usePageBackdrop(backdropUri, backdropFocalY);

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === "web") {
        // Web screens reset before their route-level reveal animation begins.
        scrollRef.current?.scrollTo({ animated: false, x: 0, y: 0 });
      }
      return undefined;
    }, [])
  );

  useEffect(() => {
    if (Platform.OS === "web") return undefined;

    // Native Stack owns its transition on UIKit/FragmentManager. JS
    // InteractionManager does not represent that native animation and may
    // run while the outgoing page is still visible. Reset only after this
    // route's real native disappearance has completed. A cancelled iOS Back
    // gesture never emits transitionEnd(closing: true), so its revealed page
    // retains the exact scroll position the user is looking at.
    const nativeNavigation = navigation as unknown as NativeTransitionEndNavigation;
    return nativeNavigation.addListener("transitionEnd", (event) => {
      if (!event.data.closing) return;
      if (keyboardAware) {
        keyboardAwareScrollRef.current?.scrollTo({
          animated: false,
          x: 0,
          y: 0
        });
      } else {
        scrollRef.current?.scrollTo({ animated: false, x: 0, y: 0 });
      }
    });
  }, [keyboardAware, navigation]);

  useFocusEffect(
    useCallback(() => {
      // While this raised layer holds focus, a backward browser traversal
      // means "dismiss the layer": the reveal beneath stays stationary, and
      // the popstate interceptor calls this dismissal to play the exit
      // BEFORE the router hears about the traversal (expo-router replays
      // browser Back as a resetRoot, which can bypass beforeRemove
      // entirely — usePreventRemove below only covers in-app removals).
      // Registration is level-based on focus/blur, so the router's trailing
      // PUSH writes can't clobber it.
      if (Platform.OS !== "web" || enter !== "rise") return undefined;
      return registerWebRiseLayer((onExitComplete) => {
        if (
          !leaveTransition(() => {
            // Disarm usePreventRemove first; the effect below replays the
            // traversal only after that disarm has committed.
            dismissalDoneRef.current = onExitComplete;
            setRemovalAllowed(true);
          })
        ) {
          dismissalDoneRef.current = onExitComplete;
          setRemovalAllowed(true);
        }
      });
    }, [enter, leaveTransition])
  );

  // Raised layers have one exit controller. Every React Navigation action
  // that removes the route — header Back, browser Back, a history gesture,
  // pop, or reset — waits for this same reverse-rise animation, then resumes
  // the exact action that requested removal.
  usePreventRemove(
    Platform.OS === "web" && enter === "rise" && !removalAllowed,
    ({ data }) => {
      if (pendingRemovalAction.current) return;
      if (
        !enterTransition.leave(() => {
          pendingRemovalAction.current = data.action;
          setRemovalAllowed(true);
        })
      ) {
        pendingRemovalAction.current = data.action;
        setRemovalAllowed(true);
      }
    }
  );

  useEffect(() => {
    if (!removalAllowed) return;
    // If browser Back raced an already-running in-app exit, the browser's
    // history has physically moved and is authoritative. Replay that entry
    // once and discard the now-redundant prevented GO_BACK action.
    if (dismissalDoneRef.current) {
      const done = dismissalDoneRef.current;
      dismissalDoneRef.current = null;
      pendingRemovalAction.current = null;
      done();
      return;
    }
    // In-app removal (header Back, pop): resume the exact prevented action.
    if (pendingRemovalAction.current) {
      const action = pendingRemovalAction.current;
      pendingRemovalAction.current = null;
      dispatchWebNavigationWithoutDirection(() => navigation.dispatch(action));
    }
  }, [navigation, removalAllowed]);

  const scrollContent = (
    <Animated.View
      style={[
        styles.column,
        { maxWidth },
        padded && (header ? styles.columnPaddingInline : styles.columnPadding),
        enterStyle
      ]}
    >
      {children}
    </Animated.View>
  );
  const scrollContentStyle = [
    styles.scrollContent,
    {
      paddingBottom: contentBottomInset ?? insets.bottom + space.xxxl,
      paddingTop: header
        ? resolvedHeaderHeight + APP_HEADER_CONTENT_GAP
        : 0
    }
  ];
  const scroll = keyboardAware && Platform.OS !== "web" ? (
    <KeyboardAwareScrollView
      ref={keyboardAwareScrollRef}
      bottomOffset={SEARCH_KEYBOARD_GAP}
      contentContainerStyle={scrollContentStyle}
      disableScrollOnKeyboardHide
      keyboardDismissMode="on-drag"
      keyboardShouldPersistTaps={keyboardShouldPersistTaps ?? "handled"}
      style={styles.scroll}
    >
      {scrollContent}
    </KeyboardAwareScrollView>
  ) : (
    <ScrollView
      ref={scrollRef}
      keyboardShouldPersistTaps={keyboardShouldPersistTaps}
      style={styles.scroll}
      contentContainerStyle={scrollContentStyle}
    >
      {scrollContent}
    </ScrollView>
  );

  return (
    <View style={styles.screen}>
      {Platform.OS !== "web" ? (
        <View style={[styles.nativeTransitionSurface, { pointerEvents: "none" }]}>
          <PageBackdrop
            focalY={backdropFocalY}
            sourceUri={
              Platform.OS === "ios"
                ? backdropUri
                : backdropUri == null
                  ? null
                  : undefined
            }
          />
        </View>
      ) : null}
      {header ? (
        <HeaderContentMask height={resolvedHeaderHeight}>{scroll}</HeaderContentMask>
      ) : (
        scroll
      )}
      {header ? (
        <View
          // Fixed header wrappers are transparent to hit testing so the real
          // page ScrollView remains wheel/trackpad-scrollable beneath them.
          // AppHeader opts only its actual controls back into pointer events.
          style={[
            styles.screenHeader,
            {
              pointerEvents: Platform.OS === "web" ? "none" : "box-none"
            }
          ]}
        >
          {Platform.OS === "android" ? (
            <HeaderBackdropScrim
              sourceUri={backdropUri == null ? null : undefined}
              style={styles.screenHeaderBackdrop}
            />
          ) : null}
          <Animated.View
            style={[
              styles.headerColumn,
              {
                maxWidth,
                pointerEvents: Platform.OS === "web" ? "none" : "box-none"
              },
              enterStyle
            ]}
          >
            {header}
          </Animated.View>
        </View>
      ) : null}
    </View>
  );
}

type ContentSectionProps = PropsWithChildren<
  (
    | { header: ReactNode; label?: never }
    | { header?: never; label: string }
  ) & {
    style?: StyleProp<ViewStyle>;
  }
>;

/**
 * A primary page section that follows a hero or other lead block.
 *
 * This component is the single owner of both pieces of page rhythm:
 * the separation above the section header and the separation between that
 * header and its content. Header components should only own their internal
 * layout (for example, the gap between a label and a search input).
 */
export function ContentSection({
  children,
  header,
  label,
  style
}: ContentSectionProps) {
  return (
    <View style={[styles.contentSection, style]}>
      {header ?? <Overline>{label}</Overline>}
      {children}
    </View>
  );
}

/** Raised panel. */
export function Card({
  children,
  style
}: PropsWithChildren<{ style?: StyleProp<ViewStyle> }>) {
  return <View style={[styles.card, style]}>{children}</View>;
}

/**
 * Quiet hairline. With a label it becomes an inline marker — used for part
 * breaks inside a recital's dance list ("Part 2"), kept deliberately subtle.
 */
export function Divider({ label }: { label?: string }) {
  if (!label) {
    return <View style={styles.hairline} />;
  }
  return (
    <View style={styles.labelRow}>
      <View style={[styles.hairline, styles.hairlineFlex]} />
      <Text variant="overline" color={palette.faint}>
        {label}
      </Text>
      <View style={[styles.hairline, styles.hairlineFlex]} />
    </View>
  );
}

/**
 * Small label chip for styles/classes; `dot` turns it into a status pill,
 * `icon` brands it (e.g. the dancer heart).
 */
export function Chip({
  children,
  tone = "neutral",
  dot = false,
  icon
}: PropsWithChildren<{
  tone?: "neutral" | "pink" | "accent" | "success" | "warning" | "danger";
  dot?: boolean;
  icon?: ReactNode;
}>) {
  const color =
    tone === "pink"
      ? palette.pink
      : tone === "accent"
        ? palette.accent
        : tone === "success"
          ? palette.success
          : tone === "warning"
            ? palette.warning
            : tone === "danger"
              ? palette.danger
              : palette.muted;
  return (
    <View
      style={[
        styles.chip,
        tone === "pink" && styles.chipPink,
        tone === "accent" && styles.chipAccent,
        tone === "success" && styles.chipSuccess,
        tone === "warning" && styles.chipWarning,
        tone === "danger" && styles.chipDanger
      ]}
    >
      {dot ? <View style={[styles.chipDot, { backgroundColor: color }]} /> : null}
      {icon}
      <Text variant="smallStrong" color={color} style={styles.chipLabel}>
        {children}
      </Text>
    </View>
  );
}

/** Program number — quiet tabular numeral in the display face. */
export function ProgramNumber({ number, size = 40 }: { number: number; size?: number }) {
  return (
    <View style={[styles.programNumber, { height: size, minWidth: size }]}>
      <Text
        color={palette.muted}
        style={{
          fontFamily: fonts.displaySemi,
          fontSize: size * 0.4,
          lineHeight: size * 0.52
        }}
      >
        {number}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1
  },
  nativeTransitionSurface: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: palette.bg
  },
  scroll: {
    flex: 1,
    // Keep wheel/trackpad momentum inside the page's actual scroll owner on
    // web. `auto` chains boundary input to the locked document viewport;
    // `contain` preserves this scroller's local elastic boundary feedback.
    ...(Platform.OS === "web"
      ? ({ overscrollBehaviorY: "contain" } as ViewStyle)
      : null)
  },
  scrollContent: {
    alignItems: "center",
    flexGrow: 1
  },
  column: {
    flex: 1,
    width: "100%"
  },
  columnPadding: {
    paddingHorizontal: Platform.select({ web: space.xl, default: space.lg }),
    paddingTop: space.lg
  },
  columnPaddingInline: {
    paddingHorizontal: Platform.select({ web: space.xl, default: space.lg })
  },
  contentSection: {
    gap: space.md,
    marginTop: space.xxl
  },
  screenHeader: {
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 2
  },
  screenHeaderBackdrop: {
    bottom: 0,
    left: 0,
    overflow: "hidden",
    position: "absolute",
    right: 0,
    top: 0
  },
  headerColumn: {
    alignSelf: "center",
    width: "100%"
  },
  card: {
    backgroundColor: palette.surface,
    borderColor: palette.hairline,
    borderRadius: radius.lg,
    borderWidth: 1,
    padding: space.xl
  },
  hairline: {
    backgroundColor: palette.hairline,
    height: StyleSheet.hairlineWidth,
    width: "100%"
  },
  hairlineFlex: {
    flex: 1,
    width: undefined
  },
  labelRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.lg,
    marginVertical: space.md
  },
  chip: {
    alignItems: "center",
    backgroundColor: palette.coolWash,
    borderRadius: radius.pill,
    flexDirection: "row",
    gap: 6,
    paddingHorizontal: space.md,
    paddingVertical: 3
  },
  chipDot: {
    borderRadius: 4,
    height: 7,
    width: 7
  },
  chipPink: {
    backgroundColor: "rgba(242, 160, 204, 0.12)"
  },
  chipAccent: {
    backgroundColor: "rgba(143, 168, 250, 0.12)"
  },
  chipSuccess: {
    backgroundColor: "rgba(142, 216, 180, 0.12)"
  },
  chipWarning: {
    backgroundColor: "rgba(242, 198, 138, 0.12)"
  },
  chipDanger: {
    backgroundColor: "rgba(242, 139, 130, 0.08)",
    borderColor: "rgba(242, 139, 130, 0.24)"
  },
  chipLabel: {
    fontSize: 12
  },
  programNumber: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 4
  }
});
